<template>
  <div class="ws" data-test="wait-settings">
    <SettingsCard title="Wait">
      <FieldRow label="Wait type" :choice="true">
        <div class="ws__choices" role="radiogroup" aria-label="Wait type">
          <label
            v-for="option in WAIT_TYPES"
            :key="option.value"
            class="ws__choice"
            :class="{ 'is-on': p.waitType === option.value }"
          >
            <input
              v-model="p.waitType"
              type="radio"
              :value="option.value"
              name="wait-type"
              :data-test="`wait-type-${option.value}`"
            />
            <span class="ws__choice-label">{{ option.label }}</span>
          </label>
        </div>
      </FieldRow>

      <FieldRow v-if="p.waitType === 'duration'" label="Duration" for-id="ws-duration">
        <div class="ws__duration">
          <input
            id="ws-duration"
            v-model="p.durationValue"
            type="number"
            min="1"
            class="ws__number"
            placeholder="1"
            data-test="wait-duration-value"
          />
          <PillTabs
            :items="unitTabs"
            :value="p.durationUnit || ''"
            aria-label="Duration unit"
            @change="onUnit"
          />
        </div>
      </FieldRow>

      <FieldRow v-if="p.waitType === 'timestamp'" label="Wait until" for-id="ws-until">
        <input
          id="ws-until"
          v-model="p.waitUntil"
          type="datetime-local"
          class="ws__input"
          data-test="wait-until"
        />
      </FieldRow>
    </SettingsCard>
  </div>
</template>

<script>
/**
 * Wait (design frame 1m): wait for a duration, or until a specific time. The classic fields: `waitType`
 * ('duration' | 'timestamp'), `durationValue`, `durationUnitObj` + `durationUnit` (the unit pills write
 * BOTH, exactly what classic's multiselect does: v-model on the object and `@input` on the value), and
 * `waitUntil`.
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID.
 */
import FieldRow from './FieldRow.vue';
import SettingsCard from './SettingsCard.vue';
import PillTabs from '@/components/PatientPanel/actions/PillTabs.vue';

const WAIT_TYPES = [
  { value: 'duration', label: 'Wait for a duration' },
  { value: 'timestamp', label: 'Wait until a specific time' },
];

export default {
  name: 'WaitSettings',
  components: { FieldRow, SettingsCard, PillTabs },
  inject: ['wfBuilder'],

  data() {
    return { WAIT_TYPES };
  },

  computed: {
    p() {
      return (this.wfBuilder.selectedNodeData || {}).properties || {};
    },
    unitTabs() {
      return (this.wfBuilder.durationUnitOptions || []).map(o => ({ id: o.value, label: o.text }));
    },
  },

  methods: {
    onUnit(id) {
      const option = (this.wfBuilder.durationUnitOptions || []).find(o => o.value === id);
      this.$set(this.p, 'durationUnitObj', option || null);
      this.$set(this.p, 'durationUnit', option ? option.value : 'hours');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../workflow-tokens';

.ws {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ws__choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 620px) { grid-template-columns: 1fr; }
}

.ws__choice {
  @include wf-choice-card;
  align-items: center;
  margin: 0;
}

.ws__choice-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: $fu-text;
}

.ws__duration {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.ws__number {
  @include wf-field(42px);
  width: 96px;
}

.ws__input { @include wf-field(42px); }
</style>
